/**
 * Lịch làm việc (WorkShift) client — khai báo ca làm việc theo Khoa/Phòng/Bác sĩ,
 * lặp theo thứ hoặc theo ngày cụ thể, tính theo buổi (sessionCode) hoặc khung giờ.
 * Dùng cho luồng đặt lịch linh hoạt (P2/P3).
 * Real /api/v1/work-shifts — real-only (mirror services.ts / bookingPolicy.ts),
 * không dùng mock adapter.
 */
import { apiClient } from "./client";

export interface WorkShift {
  id: string;
  branchId: string | null;
  departmentId: string | null;
  roomId: string | null;
  doctorProfileId: string | null;
  departmentName?: string;
  roomName?: string;
  doctorName?: string;
  /** ISO weekday 1=Thứ 2 … 7=Chủ nhật; null nếu dùng ngày cụ thể. */
  weekday: number | null;
  /** "YYYY-MM-DD"; null nếu lặp theo thứ. */
  specificDate: string | null;
  /** Mã buổi khám (ClinicSession.code); null nếu dùng khung giờ. */
  sessionCode: string | null;
  /** "HH:mm"; null nếu dùng buổi. */
  startTime: string | null;
  /** "HH:mm"; null nếu dùng buổi. */
  endTime: string | null;
  capacity: number;
  active: boolean;
}

export interface WorkShiftInput {
  departmentId?: string | null;
  roomId?: string | null;
  doctorProfileId?: string | null;
  weekday?: number | null;
  specificDate?: string | null;
  sessionCode?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  capacity: number;
  active?: boolean;
}

export interface WorkShiftFilter {
  departmentId?: string | null;
  roomId?: string | null;
  doctorId?: string | null;
  weekday?: number | null;
}

/** Nhãn thứ theo ISO (1=Thứ 2 … 7=Chủ nhật). */
export const WEEKDAY_OPTIONS: { value: number; label: string; short: string }[] = [
  { value: 1, label: "Thứ 2", short: "T2" },
  { value: 2, label: "Thứ 3", short: "T3" },
  { value: 3, label: "Thứ 4", short: "T4" },
  { value: 4, label: "Thứ 5", short: "T5" },
  { value: 5, label: "Thứ 6", short: "T6" },
  { value: 6, label: "Thứ 7", short: "T7" },
  { value: 7, label: "Chủ nhật", short: "CN" },
];

export function weekdayLabel(weekday: number | null): string {
  if (weekday == null) return "—";
  return WEEKDAY_OPTIONS.find((w) => w.value === weekday)?.label ?? String(weekday);
}

export const workShiftsApi = {
  list(filter: WorkShiftFilter = {}): Promise<WorkShift[]> {
    const params: Record<string, string | number> = {};
    if (filter.departmentId) params.departmentId = filter.departmentId;
    if (filter.roomId) params.roomId = filter.roomId;
    if (filter.doctorId) params.doctorId = filter.doctorId;
    if (filter.weekday != null) params.weekday = filter.weekday;
    return apiClient
      .get<WorkShift[]>("/v1/work-shifts", { params })
      .then((r) => r.data);
  },
  create(req: WorkShiftInput): Promise<WorkShift> {
    return apiClient
      .post<WorkShift>("/v1/work-shifts", req)
      .then((r) => r.data);
  },
  update(id: string, req: WorkShiftInput): Promise<WorkShift> {
    return apiClient
      .put<WorkShift>(`/v1/work-shifts/${id}`, req)
      .then((r) => r.data);
  },
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/work-shifts/${id}`).then(() => undefined);
  },
  /**
   * Who Lịch trực puts in this room at this moment (#18786).
   *
   * `time` is what the reception form has — it has no session field — and the backend derives the
   * session from it, so a rota written by session (which is how clinics write it) still matches.
   */
  duty(roomId: string, date: string, time?: string): Promise<RoomDuty> {
    const params: Record<string, string> = { roomId, date };
    if (time) params.time = time;
    return apiClient
      .get<RoomDuty>("/v1/work-shifts/duty", { params })
      .then((r) => r.data);
  },
};

/**
 * @param autoDoctorId the doctor to pre-fill, editable: the sole doctor on duty, or with several the
 *   default one (#19943 — the same doctor booking assigns when none is chosen). Null when nobody is
 *   on duty.
 * @param shiftManaged false means no rota governs this room at all; the form must leave whatever
 *   the user already picked alone rather than clearing it.
 */
export interface RoomDuty {
  doctorIds: string[];
  autoDoctorId: string | null;
  shiftManaged: boolean;
  ambiguous: boolean;
  /**
   * #19943 — chỉ có khi phòng KHÔNG có ai trực lúc đó: các bác sĩ đang trực một phòng khác. Ô Bác
   * sĩ mời mọi bác sĩ trừ những người này.
   */
  busyElsewhereDoctorIds?: string[];
}

export const workShiftsQueryKeys = {
  all: () => ["work-shifts"] as const,
  list: (filter: WorkShiftFilter) => ["work-shifts", filter] as const,
  duty: (roomId: string, date: string, time?: string) =>
    ["work-shifts", "duty", roomId, date, time ?? ""] as const,
};
